import { Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { legendConfigs } from './legend-configs';

@Component({
  selector: 'app-weather-legend',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './weather-legend.component.html',
  styleUrls: ['./weather-legend.component.scss']
})
export class WeatherLegendComponent {
  private translate = inject(TranslateService);

  @Input() selectedWeatherLayer: string | null = null;
  @Input() visible: boolean = false;

  /**
   * Get weather legend title HTML
   */
  getWeatherLegendTitleHtml(): string | null {
    const title = this.getWeatherLegendTitle();
    if (title) {
      return title.replace(/\n/g, '<br>');
    }
    return null;
  }

  /**
   * Get weather legend title
   */
  getWeatherLegendTitle(): string | null {
    if (!this.selectedWeatherLayer) return null;

    // Order matters! More specific keys must come before less specific ones
    // e.g., 'SumOfFallout' must be checked before 'Fallout'
    const titleKeys: { [key: string]: string } = {
      'SumOfFallout': 'SUM_OF_FALLOUT_MM',
      'SumOfSolarRadiation': 'SUM_OF_SOLAR_RADIATION_MJ',
      'SumOfActiveTemperaturesBase5': 'SUM_OF_ACTIVE_TEMPERATURES_BASE5_C',
      'SumOfActiveTemperaturesBase7': 'SUM_OF_ACTIVE_TEMPERATURES_BASE7_C',
      'SumOfActiveTemperaturesBase10': 'SUM_OF_ACTIVE_TEMPERATURES_BASE10_C',
      'SumOfActiveTemperaturesBase12': 'SUM_OF_ACTIVE_TEMPERATURES_BASE12_C',
      'SumOfActiveTemperaturesBase15': 'SUM_OF_ACTIVE_TEMPERATURES_BASE15_C',
      'PrecipitationTotal': 'PRECIPITATION_MM',
      'TemperatureAverage': 'TEMPERATURE_C',
      'TemperatureMax': 'TEMPERATURE_MAX_C',
      'TemperatureMin': 'TEMPERATURE_MIN_C',
      'Humidity': 'HUMIDITY_PERCENT',
      'Fallout': 'PRECIPITATION_MM',
      'WindSpeed': 'WIND_SPEED_MS',
      'WindDirection': 'WIND_DIRECTION_DEG',
      'PrecipitationDeviation': 'PRECIPITATION_DEVIATION_PERCENT',
      'TemperatureDeviation': 'TEMPERATURE_DEVIATION_C',
      'SolarRadiationInfluence': 'SOLAR_RADIATION_JCM2',
      'FrostDanger': 'FrostDanger'
    };

    // Find the matching key that is included in selectedWeatherLayer
    for (const [key, translationKey] of Object.entries(titleKeys)) {
      if (this.selectedWeatherLayer?.includes(key)) {
        return this.translate.instant(translationKey);
      }
    }

    return null;
  }

  /**
   * Get weather legend items
   */
  getWeatherLegendItems(): any[] {
    if (!this.selectedWeatherLayer) return [];

    // Order matters! More specific keys must come before less specific ones
    // e.g., 'SumOfFallout' must be checked before 'Fallout'
    const orderedConfigKeys = [
      'SumOfFallout',
      'SumOfSolarRadiation',
      'SumOfActiveTemperaturesBase5',
      'SumOfActiveTemperaturesBase7',
      'SumOfActiveTemperaturesBase10',
      'SumOfActiveTemperaturesBase12',
      'SumOfActiveTemperaturesBase15',
      'PrecipitationTotal',
      'Humidity',
      'Fallout',
      'TemperatureAverage',
      'TemperatureMax',
      'TemperatureMin',
      'TemperatureMinTotal',
      'TemperatureMaxTotal',
      'TemperatureAverageTotal',
      'FrostDanger',
      'WindSpeed',
      'PrecipitationDeviation',
      'TemperatureDeviation',
      'SolarRadiationInfluence'
    ];

    // Find the matching config key that is included in selectedWeatherLayer
    for (const configKey of orderedConfigKeys) {
      if (this.selectedWeatherLayer?.includes(configKey)) {
        const config = legendConfigs[configKey];
        return config ? config.items : [];
      }
    }

    // Fallback: try exact match if no partial match found
    const config = legendConfigs[this.selectedWeatherLayer];
    return config ? config.items : [];
  }

  /**
   * Get translated value for legend item
   * If the value is a translation key, translate it; otherwise return as-is
   */
  getTranslatedValue(value: string): string {
    // Check if the value looks like a translation key (uppercase with underscores)
    if (value && /^[A-Z_]+$/.test(value)) {
      return this.translate.instant(value);
    }
    return value;
  }

  /**
   * Check if the selected weather layer should display as a gradient
   */
  get isGradientWeatherLayer(): boolean {
    if (!this.selectedWeatherLayer) return false;

    const gradientLayers = [
      'Humidity',
      'PrecipitationTotal',
      'Fallout',
      'SolarRadiationInfluence',
      'TemperatureAverage',
      'TemperatureMax',
      'TemperatureMin',
      'TemperatureDeviation',
      'TemperatureMaxTotal',
      'TemperatureMinTotal',
      'SumOfActiveTemperaturesBase5',
      'SumOfActiveTemperaturesBase7',
      'SumOfActiveTemperaturesBase10',
      'SumOfActiveTemperaturesBase12',
      'SumOfActiveTemperaturesBase15',
      'SumOfFallout',
      'SumOfSolarRadiation'
      // FrostDanger removed - now uses discrete scale
    ];

    return gradientLayers.some(layer => this.selectedWeatherLayer!.includes(layer));
  }

  /**
   * Check if the selected weather layer should display as discrete scale
   */
  get isDiscreteScaleWeatherLayer(): boolean {
    return !this.isGradientWeatherLayer;
  }

  /**
   * Get the base name of the weather layer for CSS class
   */
  get weatherLayerBaseName(): string {
    if (!this.selectedWeatherLayer) return '';

    // Order matters! More specific names must come before less specific ones
    // e.g., 'SumOfFallout' must be checked before 'Fallout'
    const layerNames = [
      'SumOfFallout',
      'SumOfSolarRadiation',
      'SumOfActiveTemperaturesBase5',
      'SumOfActiveTemperaturesBase7',
      'SumOfActiveTemperaturesBase10',
      'SumOfActiveTemperaturesBase12',
      'SumOfActiveTemperaturesBase15',
      'Humidity',
      'PrecipitationTotal',
      'Fallout',
      'SolarRadiationInfluence',
      'TemperatureAverage',
      'TemperatureMax',
      'TemperatureMin',
      'TemperatureDeviation',
      'TemperatureMaxTotal',
      'TemperatureMinTotal',
      'WindSpeed',
      'WindDirection',
      'PrecipitationDeviation',
      'FrostDanger'
    ];

    for (const layerName of layerNames) {
      if (this.selectedWeatherLayer.includes(layerName)) {
        return layerName.toLowerCase();
      }
    }

    return this.selectedWeatherLayer.toLowerCase();
  }

  /**
   * Check if legend should be visible
   */
  get isVisible(): boolean {
    return !!(this.visible && this.selectedWeatherLayer && this.getWeatherLegendTitleHtml());
  }

  /**
   * Check if this layer needs a wider legend (for large numbers)
   */
  get needsWideLegend(): boolean {
    if (!this.selectedWeatherLayer) return false;
    const wideLayerNames = ['SumOfSolarRadiation'];
    return wideLayerNames.some(name => this.selectedWeatherLayer!.includes(name));
  }
}
